/**
 * ==== STANDALONE:FARMHAND_PRO:START ====
 * Feature: Farm reports API - batch completion and annual year-end reports
 * Tier: Pro ($7/mo)
 * Dependencies: portalAPI (axios instance)
 * Generalization: Replace /portal/reports/ paths with standalone API paths.
 *   Report generation logic is portable for any farm operation.
 * ============================================
 *
 * Reports API Service
 *
 * Handles batch completion reports and annual year-end reports.
 */

import portalAPI from './portalAPI';

// Types
export interface GeneratedReport {
  id: string;
  report_type: 'batch' | 'annual';
  title: string;
  description: string;
  file_name: string;
  file_size_bytes: number | null;
  generated_at: string;
  batch_id: string | null;
  year: number | null;
  download_url: string;
}

export interface ReportListResponse {
  reports: GeneratedReport[];
  total: number;
  limit: number;
  offset: number;
}

export interface AvailableYearsResponse {
  years: number[];
  current_year: number;
}

// API functions
export const reportsAPI = {
  /**
   * Get list of previously generated reports
   */
  listReports: (params?: {
    type?: 'batch' | 'annual';
    limit?: number;
    offset?: number;
  }) => portalAPI.get<ReportListResponse>('/planner/portal/reports/', { params }),

  /**
   * Get available years for annual reports (years that have batch data)
   */
  getAvailableYears: () =>
    portalAPI.get<AvailableYearsResponse>('/planner/portal/reports/available-years/'),

  /**
   * Download a batch completion report
   * Returns a blob that can be downloaded
   */
  downloadBatchReport: async (batchId: string, save: boolean = true): Promise<Blob> => {
    const response = await portalAPI.get(
      `/planner/portal/batches/${batchId}/report/pdf/`,
      {
        params: { save: save ? 'true' : 'false' },
        responseType: 'blob'
      }
    );
    return response.data;
  },

  /**
   * Download an annual year-end report
   * Returns a blob that can be downloaded
   */
  downloadAnnualReport: async (year: number, save: boolean = true): Promise<Blob> => {
    const response = await portalAPI.get(
      `/planner/portal/reports/annual/${year}/pdf/`,
      {
        params: { save: save ? 'true' : 'false' },
        responseType: 'blob'
      }
    );
    return response.data;
  },

  /**
   * Download a previously generated report by ID
   */
  downloadReport: async (reportId: string): Promise<Blob> => {
    const response = await portalAPI.get(
      `/planner/portal/reports/${reportId}/download/`,
      { responseType: 'blob' }
    );
    return response.data;
  },
};

// Helper function to trigger browser download
export const triggerDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export default reportsAPI;
// ==== STANDALONE:FARMHAND_PRO:END ====
